import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/services/auth.service';
import { SessionTimeoutService } from '../core/services/session-timeout.service';
import { SessionTimeoutState } from '../core/enums/SessionTimeoutState';
import { ShouldSkipAuthInterceptor } from '../helpers/http-interceptor-helpers';

@Injectable(
  {providedIn: 'root'}
)
export class AuthInterceptor implements HttpInterceptor {
  
  constructor( private readonly authService : AuthService, private readonly sessionTimeoutService : SessionTimeoutService){}
  

  /**
   * Intercepts HTTP requests to add authentication token and handle errors.
   * @param req The outgoing HTTP request.
   * @param next The next handler in the chain.
   * @returns An observable of the HTTP event.
   */
  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.addAuthToken(req).pipe(
      switchMap(authedReq => next.handle(authedReq)),
      catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
    );
  }


  /**
   * Adds the authentication token to the request if available and refreshes the token if necessary.
   * @param req The outgoing HTTP request.
   * @returns An observable of the modified HTTP request.
   */
  private addAuthToken(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    if (!ShouldSkipAuthInterceptor(req.context) && this.shouldRefreshToken()) {
      return this.authService.RefreshToken().pipe(
        switchMap(() => this.createAuthRequest(req)),
         catchError((refreshError) => {
          
          // Auth may fail if another tab has already refreshed the token
          // As long as we aren't expired, we can run the request as normal
          // And then refresh the token on the next request if needed
          // If we are expired, log out the user
          if(this.sessionTimeoutService.getSessionState() == SessionTimeoutState.Expired){
            this.authService.logOut();
            return throwError(() => refreshError);
          }
          return this.createAuthRequest(req);          
        })
      );
    } else {
      return this.createAuthRequest(req);
    }
  }

  /**
   * Creates a new HTTP request with the authentication token if available.
   * @param req The outgoing HTTP request.
   * @returns An observable of the modified HTTP request.
   */
  private createAuthRequest(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    const access_token = this.authService.access_token;

    if (access_token != null && (req.url.startsWith(environment.apiUrl) || req.url.startsWith(environment.legacyApiUrl))) {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + access_token)
      });
      return new Observable(observer => observer.next(authReq));
    }

    return new Observable(observer => observer.next(req));
  }
  

    /**
   * Handles HTTP errors, specifically 401 errors by attempting to refresh the token.
   * @param error The HTTP error response.
   * @param req The outgoing HTTP request.
   * @param next The next handler in the chain.
   * @returns An observable of the HTTP event.
   */
    private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      if (error.status === 401) {
        return this.authService.RefreshToken().pipe(    
          switchMap(() => this.createAuthRequest(req)),
          switchMap(refreshedAuthReq => next.handle(refreshedAuthReq)),
          catchError((refreshError) => {
            this.authService.logOut();
            return throwError(() => refreshError);
          })
        );
      }
      return throwError(() => error);
    }

    /**
   * Checks if the authentication token should be refreshed based on its expiry time.
   * @returns A boolean indicating whether the token should be refreshed.
   */
    private shouldRefreshToken(): boolean {
      const access_token = this.authService.access_token;

      if (access_token == null) {
        return false;
      }

      const refreshState = this.sessionTimeoutService.getSessionState();

      return refreshState != SessionTimeoutState.Fine;
    }


  /**
   * Creates a new request with the authentication token added.
   * @param req The outgoing HTTP request.
   * @returns An observable of the modified HTTP request.
   */

  // public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

  //   const authedReq = this.tryAddAuthTokenToRequest(req); 
  //   return next.handle(authedReq).pipe(
  //     catchError((error : HttpErrorResponse) => {
  //       if(error.status === 401){
  //         // Attempt to refresh the token
  //         return this.authService.RefreshToken().pipe(
  //           switchMap(() => {
  //             // update the request with the new token
  //             const refreshedAuthReq = this.tryAddAuthTokenToRequest(req);
  //             // resend the request
  //             return next.handle(refreshedAuthReq);                  
  //           }),
  //           catchError((refreshError) => {
  //             // If the refresh token fails, log out
  //             this.authService.logOut();
  //             return throwError(() => refreshError)
  //           }));
  //       }
  //       return throwError(() => error);            
  //     })
  //   );
  // }

  // private handleRequest

  // /**
  //  * If an auth token is available, add it to the request headers   
  //  * otherwise just fire the request as is
  //  * @param req outgoing request
  //  * @param access_token 
  //  */
  // private tryAddAuthTokenToRequest(req: HttpRequest<any>) : HttpRequest<any> {
  //       // Get the auth token from the service.
  //       const access_token = this.authService.access_token;

  //       if (access_token != null && (req.url.startsWith(environment.apiUrl))) {
  //         // Clone the request and replace the original headers with
  //         // cloned headers, updated with the authorization.
  //         const authReq = req.clone({
  //           headers: req.headers.set('Authorization', 'Bearer ' + access_token)
  //         });   
    
  //         // send cloned request with header to the next handler.
  //         return authReq; 
  //       }
    
  //       return req;
  // }
} 